// REACT
import React, {
	Dispatch,
	SetStateAction,
	useState,
	useRef,
	MutableRefObject,
	useEffect,
	RefObject,
	useCallback,
	useContext,
} from 'react'

// DAY PICKER
import DayPicker, { DateUtils } from 'react-day-picker'
import 'react-day-picker/lib/style.css'

// ESTILOS
import Styles from './DatePicker.module.css'

// GLOBALES
import { tomorrow } from 'LocalGlobals/Locals'

// HERRAMIENTAS
import { formatDate } from 'Utils/Tools'

// CONTEXTO
import MainContext from 'Context/MainContext'

// PROPIEDADES
interface IProps {
	clearData?: boolean
	className?: string
	color?: string
	getData?: (data: IDateFormData) => any
}

// ESTADO
interface IState {
	from: Date | null
	to: Date | null
	enteredTo: Date | null
	adults: number
	children: number
}

// ESTADO POR DEFECTO
const DefState: IState = {
	from: null,
	to: null,
	enteredTo: null,
	adults: 0,
	children: 0,
}

const DatePicker: React.FC<IProps> = (props: IProps) => {
	// CONTEXTO
	const { lang } = useContext(MainContext)
	const { datepicker, months, daysShort } = lang

	// ESTADO
	const [dateState, setDates]: [IState, Dispatch<SetStateAction<IState>>] = useState(DefState)
	const { from, to, enteredTo } = dateState

	// REFERENCIAS
	const dateRef: MutableRefObject<HTMLDivElement | null> = useRef(null)
	const groupRef: RefObject<HTMLDivElement> = useRef(null)

	// VERIFICAR PRIMER DIA
	const isSelectingFirstDay = (fromS: Date | null, toS: Date | null, dayS: Date | null) => {
		// @ts-ignore
		const isBeforeFirstDay: boolean | null = fromS && DateUtils.isDayBefore(dayS, fromS)
		const isRangeSelected: Date | null = fromS && toS

		// RETORNAR SI HAY DIAS ANTERIORES
		return !fromS || isBeforeFirstDay || isRangeSelected
	}

	// ASIGNAR DIAS EN HOVER
	const handleDayMouseEnter = (day: Date) => {
		if (!isSelectingFirstDay(from, to, day))
			setDates({
				...dateState,
				enteredTo: day,
			})
	}

	// MOSTRAR/OCULTAR PICKER
	const togglePicker = useCallback(
		(hide: boolean) =>
			dateRef.current
				? (dateRef.current.style.transform = `scale(${hide ? 0 : 1}) translateX(-50%)`)
				: '',
		[]
	)

	// MOSTRAR/OCULTAR GROUP PICKER
	const toggleGroupPicker = useCallback(
		(hide: boolean) =>
			groupRef.current ? (groupRef.current.style.transform = `scale(${hide ? 0 : 1})`) : '',
		[]
	)

	// MOSTRAR PICKER
	const showPicker = useCallback(() => togglePicker(false), [togglePicker])

	// OCULTAR PICKER
	const hidePicker = useCallback(() => togglePicker(true), [togglePicker])

	// MOSTRAR CONTROL DE PERSONAS
	const showGroupPicker = useCallback(() => toggleGroupPicker(false), [toggleGroupPicker])

	// OCULTAR CONTROL DE PERSONAS
	const hideGroupPicker = useCallback(() => toggleGroupPicker(true), [toggleGroupPicker])

	// REINICIAR DIAS
	const handleResetClick = () => setDates(DefState)

	// OBTENER DIAS
	const handleDayClick = (day: Date) => {
		// REINICIAR DIAS
		if (from && to && day >= from && day <= to) handleResetClick()

		// REINICIAR PRIMER DIA
		if (isSelectingFirstDay(from, to, day))
			setDates({
				...DefState,
				from: day,
				adults: dateState.adults,
				children: dateState.children,
			})
		// SINO ASIGNAR DIAS SIGUIENTES
		else {
			// OCULTAR CONTAINER
			hidePicker()

			// ACTUALIZAR ESTADO
			sendData([dateState.from, day], [dateState.adults, dateState.children])
			setDates({
				...dateState,
				to: day,
				enteredTo: day,
			})
		}
	}

	// ENVIAR DATOS
	const sendData = useCallback((dates: (Date | null)[], groups: number[]) => {
		// GUARDAR DATOS
		window.localStorage.setItem('from', dates[0]?.toString() || '')
		window.localStorage.setItem('to', dates[1]?.toString() || '')
		window.localStorage.setItem('adults', groups[0].toString())
		window.localStorage.setItem('children', groups[1].toString())

		// ENVIAR AL FORMULARIO
		if (props.getData)
			props.getData({
				date: {
					from: dates[0],
					to: dates[1],
				},
				group: {
					adults: groups[0],
					children: groups[1],
				},
			})
	}, [props])

	// CAMBIAR PERSONAS
	const changeGroup = (mode: number) => {
		// AGREGAR O REMOVER DE ADULTOS ..
		const adultsN: number = mode === 0 ? 1 : mode === 2 ? (dateState.adults > 0 ? -1 : 0) : 0
		const children: number = mode === 1 ? 1 : mode === 3 ? (dateState.children > 0 ? -1 : 0) : 0

		// ENVIAR NUEVOS DATOS
		sendData(
			[dateState.from, dateState.to],
			[dateState.adults + adultsN, dateState.children + children]
		)

		// ACTUALIZAR ESTADO
		setDates({
			...dateState,
			adults: dateState.adults + adultsN,
			children: dateState.children + children,
		})
	}

	// CAMBIAR ADULTOS
	const addAdult = () => changeGroup(0)
	const removeAdult = () => changeGroup(2)

	// CAMBIAR HIJOS
	const addChild = () => changeGroup(1)
	const removeChild = () => changeGroup(3)

	useEffect(() => {
		// SELECCIONAR DATE PICKER
		const rangeContainer: HTMLDivElement = document.querySelector('.Range') as HTMLDivElement

		// ASIGNAR A REFERENCIAS
		dateRef.current = rangeContainer

		window.addEventListener('click', (ev: MouseEvent) => {
			// ELEMENTO
			const el: HTMLElement = ev.target as HTMLElement

			// VERIFICAR QUE NO SEA EL CONTAINER
			if (
				(!rangeContainer.contains(el) &&
					!el.classList.value.includes('openPickerBtn') &&
					!el.classList.value.includes('openPickerIcon')) ||
				el.classList.value.includes('openGroupBtn') ||
				el.classList.value.includes('iDown')
			)
				hidePicker()

			// VERIFICAR QUE NO SEA EL CONTAINER GROUP
			if (
				(!groupRef.current?.contains(el) &&
					!el.classList.value.includes('openPickerBtn') &&
					!el.classList.value.includes('openPickerIcon')) ||
				el.classList.value.includes('openDateBtn') ||
				el.classList.value.includes('iUp')
			)
				hideGroupPicker()
		})
	}, [hidePicker, hideGroupPicker])

	// LIMPIAR DATOS
	useEffect(() => {
		if (props.clearData) handleResetClick()
	}, [props.clearData])

	// OBTENER DATOS
	useEffect(() => {
		// LEER DATOS
		const fromLST: string = window.localStorage.getItem('from') || ''
		const toLST: string = window.localStorage.getItem('to') || ''
		const fromD: Date = new Date(fromLST)
		const toD: Date = new Date(toLST)
		const adults: number = parseInt(window.localStorage.getItem('adults') || '', 10)
		const children: number = parseInt(window.localStorage.getItem('children') || '', 10)

		// ACTUALIZAR DATOS
		if (fromLST.length && toLST.length) {
			sendData([fromD, toD], [adults, children])

			// ACTUALIZAR ESTADOS
			setDates({ ...DefState, from: fromD, to: toD, children, adults, enteredTo: toD })
		}
	}, [sendData])

	// MODIFICADORES, DIAS DESHABILITADOS Y SELECCIONADOS
	const selectedDays = [from, { from, to: enteredTo }]
	const modifiers = {
		start: from,
		end: enteredTo,
	}
	const disabledDays = {
		before: from || new Date(),
	}

	return (
		<div
			style={{ borderColor: props.color }}
			className={`${Styles.pickersContainer}${props.className ? ` ${props.className}` : ''}`}>
			<button className={`${Styles.openPickerBtn} openDateBtn`} onClick={showPicker}>
				<svg
					className={`${Styles.openPickerIcon} ${Styles.iUp}`}
					version='1.1'
					x='0px'
					y='0px'
					viewBox='0 0 477.867 477.867'
					fill='var(--iconColor)'>
					<g>
						<g>
							<path
								d='M426.667,51.2h-51.2V17.067C375.467,7.641,367.826,0,358.4,0s-17.067,7.641-17.067,17.067V51.2h-204.8V17.067
			C136.533,7.641,128.892,0,119.467,0S102.4,7.641,102.4,17.067V51.2H51.2C22.923,51.2,0,74.123,0,102.4v324.267
			c0,28.277,22.923,51.2,51.2,51.2h375.467c28.277,0,51.2-22.923,51.2-51.2V102.4C477.867,74.123,454.944,51.2,426.667,51.2z
			 M443.733,426.667c0,9.426-7.641,17.067-17.067,17.067H51.2c-9.426,0-17.067-7.641-17.067-17.067V204.8h409.6V426.667z
			 M443.733,170.667h-409.6V102.4c0-9.426,7.641-17.067,17.067-17.067h51.2v34.133c0,9.426,7.641,17.067,17.067,17.067
			s17.067-7.641,17.067-17.067V85.333h204.8v34.133c0,9.426,7.641,17.067,17.067,17.067s17.067-7.641,17.067-17.067V85.333h51.2
			c9.426,0,17.067,7.641,17.067,17.067V170.667z'
							/>
						</g>
					</g>
					<g>
						<g>
							<path
								d='M136.533,238.933H102.4c-9.426,0-17.067,7.641-17.067,17.067c0,9.426,7.641,17.067,17.067,17.067h34.133
			c9.426,0,17.067-7.641,17.067-17.067C153.6,246.574,145.959,238.933,136.533,238.933z'
							/>
						</g>
					</g>
					<g>
						<g>
							<path
								d='M256,238.933h-34.133c-9.426,0-17.067,7.641-17.067,17.067c0,9.426,7.641,17.067,17.067,17.067H256
			c9.426,0,17.067-7.641,17.067-17.067C273.067,246.574,265.426,238.933,256,238.933z'
							/>
						</g>
					</g>
					<g>
						<g>
							<path
								d='M375.467,238.933h-34.133c-9.426,0-17.067,7.641-17.067,17.067c0,9.426,7.641,17.067,17.067,17.067h34.133
			c9.426,0,17.067-7.641,17.067-17.067C392.533,246.574,384.892,238.933,375.467,238.933z'
							/>
						</g>
					</g>
					<g>
						<g>
							<path
								d='M136.533,307.2H102.4c-9.426,0-17.067,7.641-17.067,17.067c0,9.426,7.641,17.067,17.067,17.067h34.133
			c9.426,0,17.067-7.641,17.067-17.067C153.6,314.841,145.959,307.2,136.533,307.2z'
							/>
						</g>
					</g>
					<g>
						<g>
							<path
								d='M256,307.2h-34.133c-9.426,0-17.067,7.641-17.067,17.067c0,9.426,7.641,17.067,17.067,17.067H256
			c9.426,0,17.067-7.641,17.067-17.067C273.067,314.841,265.426,307.2,256,307.2z'
							/>
						</g>
					</g>
					<g>
						<g>
							<path
								d='M375.467,307.2h-34.133c-9.426,0-17.067,7.641-17.067,17.067c0,9.426,7.641,17.067,17.067,17.067h34.133
			c9.426,0,17.067-7.641,17.067-17.067C392.533,314.841,384.892,307.2,375.467,307.2z'
							/>
						</g>
					</g>
					<g>
						<g>
							<path
								d='M136.533,375.467H102.4c-9.426,0-17.067,7.641-17.067,17.067S92.974,409.6,102.4,409.6h34.133
			c9.426,0,17.067-7.641,17.067-17.067S145.959,375.467,136.533,375.467z'
							/>
						</g>
					</g>
					<g>
						<g>
							<path
								d='M256,375.467h-34.133c-9.426,0-17.067,7.641-17.067,17.067s7.641,17.067,17.067,17.067H256
			c9.426,0,17.067-7.641,17.067-17.067S265.426,375.467,256,375.467z'
							/>
						</g>
					</g>
					<g>
						<g>
							<path
								d='M375.467,375.467h-34.133c-9.426,0-17.067,7.641-17.067,17.067s7.641,17.067,17.067,17.067h34.133
			c9.426,0,17.067-7.641,17.067-17.067S384.892,375.467,375.467,375.467z'
							/>
						</g>
					</g>
				</svg>
				{`${formatDate(from || new Date(), lang)} - ${formatDate(to || tomorrow, lang)}`}
			</button>

			<button className={`${Styles.openPickerBtn} openGroupBtn`} onClick={showGroupPicker}>
				<i className={`${Styles.openPickerIcon} ${Styles.iDown} lni lni-users`} />
				{`${dateState.adults} ${datepicker.group.adults.toLowerCase()} • ${
					dateState.children
				} ${datepicker.group.children.toLowerCase()}`}
			</button>

			<div className={Styles.groupPicker} ref={groupRef}>
				<div className={Styles.groupRow}>
					<button
						style={{
							opacity: dateState.adults === 0 ? '0.8' : '1',
							cursor: dateState.adults === 0 ? 'not-allowed' : 'pointer',
						}}
						className={`${Styles.groupRemove} ${Styles.groupBtn}`}
						onClick={removeAdult}>
						<span>-</span>
					</button>
					{`${datepicker.group.adults}: ${dateState.adults}`}
					<button className={`${Styles.groupAdd} ${Styles.groupBtn}`} onClick={addAdult}>
						<span>+</span>
					</button>
				</div>
				<div className={Styles.groupRow}>
					<button
						style={{
							opacity: dateState.children === 0 ? '0.8' : '1',
							cursor: dateState.children === 0 ? 'not-allowed' : 'pointer',
						}}
						className={`${Styles.groupRemove} ${Styles.groupBtn}`}
						onClick={removeChild}>
						<span>-</span>
					</button>
					{`${datepicker.group.children}: ${dateState.children}`}
					<button className={`${Styles.groupAdd} ${Styles.groupBtn}`} onClick={addChild}>
						<span>+</span>
					</button>
				</div>
			</div>

			{/*
			 // @ts-ignore */}
			<DayPicker
				className='Range'
				numberOfMonths={2}
				months={months}
				weekdaysShort={daysShort.map((dayS: string) => dayS.substr(0, 2))}
				fromMonth={dateState.from}
				selectedDays={selectedDays}
				disabledDays={disabledDays}
				modifiers={modifiers}
				onDayClick={handleDayClick}
				onDayMouseEnter={handleDayMouseEnter}
			/>
		</div>
	)
}

export default DatePicker
